import { useNavigate } from 'react-router-dom'
import { compact, delay } from 'lodash'
import {
  Children,
  createContext,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import type { BlockerFunction } from 'react-router-dom'
import { useMutation } from 'urql'

import Button from './Button'
import CircularProgressBar from './CircularProgressBar'
import ProgressBar from './ProgressBar'
import { useBeforeunload } from 'app/hooks/useBeforeunload'
import type { Activity } from 'app/types/entities'
import type { LocationType } from 'app/types/entities'
import { notifyError } from 'app/services/notify.client'
import { gqlMutation } from 'app/lib/graphqlClient'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { CREATE_IMPORT_MUTATION, CREATE_FILE_IMPORT_MUTATION } from 'app/components/imports/graphql'

import { asyncParallel } from 'lib/asyncParallel'
import { debug, logError, logWarn } from 'lib/logging'

export type ImportFile = {
  workspace: string
  location: string
  vendor: string
  activityType: Activity[`type`] | string
  name: string
  activityDate: string
  requestedAppointmentDate: string
  phone: string
  email: string | undefined
  scheduled: `yes` | `no`
  requestedTreatment: string | undefined
  channel?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  leadOwner: string | undefined
  contactOwner?: string
  workspaceId: string | undefined
  locationId: string | undefined
  locationName: string | undefined
  lineNumber: number
}

export type ErrorRow = {
  [key in `message` | `rowNumber`]: number | string
}
type Result = { [key in `importIds`]: string[] } & { [key in `error`]: ErrorRow[] }

export type ImportWizardContextType = {
  activeStep: number
  totalSteps: number
  csvFile: File | undefined
  disablePrev: boolean
  disableNext: boolean
  dataToImport: ImportFile[]
  columnMapping: { [key: string]: string }[]
  locationWorkspaceMapping: { [key: string]: string }[]
  errors: number
  openErrorModal: boolean
  importResults: Result
  importing: boolean
  progress: number
  setActiveStep: (step: number) => void
  setCsvFile: (file?: File) => void
  setDisablePrev: (disable: boolean) => void
  setDisableNext: (disable: boolean) => void
  setDataToImport: (data: ImportFile[]) => void
  setColumnMapping: (data: { [key: string]: string }[]) => void
  setLocationWorkspaceMapping: Dispatch<SetStateAction<{ [key: string]: string }[]>>
  setErrors: (data: number) => void
  setOpenErrorModal: (data: boolean) => void
  setImportResults: (data: Result) => void
  setImporting: (data: boolean) => void
  setProgress: (data: number) => void
  onComplete: () => void
}

export const ImportWizardContext = createContext({
  activeStep: 1,
  totalSteps: 1,
  csvFile: undefined,
  disablePrev: false,
  disableNext: false,
  dataToImport: [],
  columnMapping: [],
  locationWorkspaceMapping: [],
  errors: 0,
  openErrorModal: false,
  importResults: { importIds: [], error: [] },
  importing: false,
  progress: 0,
  setActiveStep: (step: number) => {},
  setCsvFile: (file: File) => {},
  setDisablePrev: (disable: boolean) => {},
  setDisableNext: (disable: boolean) => {},
  setDataToImport: (data: ImportFile[] | string | undefined) => [],
  setColumnMapping: (data: { [key: string]: string }[]) => [],
  setLocationWorkspaceMapping: () => {},
  setErrors: (data: number) => {},
  setOpenErrorModal: (data: boolean) => {},
  setImportResults: (data: Result) => {},
  setImporting: (data: boolean) => {},
  setProgress: (data: number) => {},
  onComplete: () => {},
} as ImportWizardContextType)

type ImportWizardProps = {
  title: string
  totalSteps: number
  children: React.ReactNode
  onComplete: () => void
}

function ImportWizard({ title, totalSteps, onComplete, children }: ImportWizardProps): React.ReactElement {
  let [activeStep, setActiveStep] = useState<number>(1)
  let [csvFile, setCsvFile] = useState<File | undefined>()
  let [disablePrev, setDisablePrev] = useState<boolean>(false)
  let [disableNext, setDisableNext] = useState<boolean>(true)
  let [dataToImport, setDataToImport] = useState<ImportFile[]>([])
  let [columnMapping, setColumnMapping] = useState<{ [key: string]: string }[]>([])
  let [locationWorkspaceMapping, setLocationWorkspaceMapping] = useState<{ [key: string]: string }[]>([])
  let [errors, setErrors] = useState<number>(0)
  let [openErrorModal, setOpenErrorModal] = useState<boolean>(false)
  let [importResults, setImportResults] = useState<Result>({
    importIds: [],
    error: [],
  })
  let [importing, setImporting] = useState<boolean>(false)
  let [progress, setProgress] = useState<number>(0)
  let navigation = { state: `idle`, location: undefined as { pathname: string } | undefined }
  let shouldBlock = useCallback<BlockerFunction>(() => activeStep === totalSteps - 1, [activeStep])
  const blocker = { state: 'unblocked' as string, reset: () => {}, proceed: () => {} }

  const context = {
    activeStep,
    totalSteps,
    csvFile,
    disablePrev,
    disableNext,
    dataToImport,
    columnMapping,
    locationWorkspaceMapping,
    errors,
    openErrorModal,
    importResults,
    importing,
    progress,
    setActiveStep,
    setCsvFile: setCsvFile,
    setDisablePrev,
    setDisableNext,
    setDataToImport,
    setColumnMapping,
    setLocationWorkspaceMapping,
    setErrors,
    setOpenErrorModal,
    setImportResults,
    setImporting,
    setProgress,
    onComplete,
  }

  useEffect(() => {
    if (activeStep === totalSteps && blocker.state === `blocked`) {
      blocker.reset()
      navigation?.location?.pathname === window.location.pathname && onComplete()
    }
  }, [navigation])

  useEffect(() => {
    if (blocker.state === `blocked`) {
      notifyError(`Import process in progress, please stay in this page.`)
    }
  }, [blocker])

  useBeforeunload((e) => {
    if (activeStep > 1 && activeStep < totalSteps && navigation.state !== `idle`) {
      e.preventDefault()
    }
  })

  return (
    // oxlint-disable-next-line react/jsx-no-constructed-context-values -- context value is intentionally assembled here
    <ImportWizardContext.Provider value={context}>
      <div className="flex h-full flex-col">
        <h2 className="mb-[50px] px-10">{title}</h2>
        {children}
      </div>
    </ImportWizardContext.Provider>
  )
}

export const ImportWizardSteps = ({ children }) => {
  const { activeStep, importing, progress } = useContext(ImportWizardContext)
  const pages = Children.toArray(children)
  const currentStep = pages[activeStep - 1] as React.ReactElement
  return (
    <div className="flex grow flex-col overflow-auto px-10">
      {importing ? (
        <div className="flex h-full items-center justify-center">
          <CircularProgressBar progress={progress} />
        </div>
      ) : (
        currentStep
      )}
    </div>
  )
}

export const ImportWizardButtons = () => {
  const {
    activeStep,
    totalSteps,
    disablePrev,
    disableNext,
    errors,
    dataToImport,
    csvFile,
    importResults,
    setActiveStep,
    setOpenErrorModal,
    setErrors,
    setDisablePrev,
    setDisableNext,
    setImportResults,
    setImporting,
    setProgress,
    onComplete,
  } = useContext(ImportWizardContext)
  let navigate = useNavigate()
  const [, executeCreateFileImport] = useMutation(CREATE_FILE_IMPORT_MUTATION)

  // gqlMutation is used here instead of useMutation because _importActivity is called
  // in a batch loop via asyncParallel — useMutation hooks aren't designed for concurrent invocations.
  let _importActivity = (activityToImport: ImportFile, updatedResult: Result, rowNumber) => {
    if (!activityToImport.locationId) return Promise.reject(new Error(`Import location is required`))
    const input = {
      name: activityToImport.name,
      type: activityToImport.activityType,
      locationId: activityToImport.locationId,
      phone: activityToImport.phone,
      email: activityToImport.email || undefined,
      vendor: activityToImport.vendor,
      treatment: activityToImport.requestedTreatment || undefined,
      utmSource: activityToImport.utmSource || undefined,
      utmMedium: activityToImport.utmMedium || undefined,
      utmCampaign: activityToImport.utmCampaign || undefined,
      channel: activityToImport.channel || undefined,
      staff: activityToImport.leadOwner || undefined,
      isScheduled: activityToImport.scheduled === `yes`,
      activityDate: activityToImport.activityDate,
      requestedAppointmentDate: activityToImport.requestedAppointmentDate || undefined,
    }
    return gqlMutation(CREATE_IMPORT_MUTATION, { input })
      .then(async (res) => {
        if (res.error) {
          logError(
            res.error.message,
            `Import could not be completed on following data: ${JSON.stringify(activityToImport)}`,
          )
          notifyError(`There was an error importing: ${res.error.message}`)
          return
        }
        const data = res.data?.createImport
        if (data) {
          updatedResult.importIds.push(data.id!)
        } else {
          updatedResult.error.push({ rowNumber, message: `Duplicated entry` })
        }
      })
      .catch((err) => {
        logError(err, `Import could not be completed on following data: ${JSON.stringify(activityToImport)}`)
        updatedResult.error.push({
          message: err.message,
          rowNumber,
        })
        return null
      })
  }

  let handleImport = async () => {
    setDisableNext(true)
    let dataToImportLength = dataToImport.length
    let locations: Record<string, LocationType> = {}
    let updatedResult: Result = { importIds: [], error: [] }
    for (let i = 0; i < dataToImportLength; i += 7) {
      setProgress(Math.ceil((i / dataToImportLength) * 100))
      const elementsToImport = dataToImport.slice(i, i + 7)
      // build query of location names to fetch
      // if a location name is duplicated, it will be fetched only once
      // if a user changed the location in step 3, we should not fetch it, so we check if it has locationId and workspaceId
      const locationNames = compact(
        elementsToImport?.map((element) =>
          !(element.locationId && element.workspaceId) && !locations[element.locationName!.toLowerCase()]
            ? element.locationName
            : ``,
        ),
      ).reduce((acc, name) => {
        if (acc.includes(name)) return acc
        if (acc === ``) return `"${name}"`
        return `${acc} OR "${name}"`
      }, ``)
      // if current batch does not have any location name, skip location fetching
      if (locationNames.length !== 0) {
        let fetchedLocations = await gqlFetchList<LocationType>(
          'locations',
          { search: `name:(${encodeURIComponent(locationNames)})` },
          'id name',
        )
        if (!fetchedLocations.hits.length) {
          logWarn(
            `Location error`,
            `No locations found for: ${locationNames}`,
            `Import could not be completed on following data: ${JSON.stringify(dataToImport)}`,
          )
          updatedResult.error.push({
            message: `No locations found for: ${locationNames}`,
            rowNumber: i + 1,
          })
          throw new Error(`Locations ${locationNames} with error`)
        } else {
          fetchedLocations.hits.forEach(
            (location: LocationType) =>
              (locations = {
                ...locations,
                [`${location.name!.toLowerCase()}`]: { id: location.id, workspaceId: location.workspaceId },
              }),
          )
        }
      }
      debug(`imports`, `import activities - fetched locations`, locations)
      await asyncParallel(elementsToImport, async (element, index) => {
        let rowNumber = index + i + 1
        let activityToImport = { ...element }
        // if a user changed the location in step 3, we have to check if it has locationId and workspaceId
        if (activityToImport.locationId) {
          return _importActivity(activityToImport, updatedResult, rowNumber)
        } else {
          let fetchedLocation: LocationType = locations[element.locationName!.toLowerCase()]
          if (fetchedLocation && fetchedLocation.workspaceId === activityToImport.workspaceId) {
            activityToImport.locationId = fetchedLocation.id
            return _importActivity(activityToImport, updatedResult, rowNumber)
          } else {
            updatedResult.error.push({
              message: `Location ${element.locationName} not found for workspace ${activityToImport.workspace}`,
              rowNumber,
            })
          }
        }
      })
    }
    debug(`imports`, `result`, updatedResult)
    if (updatedResult.importIds.length > 0 && csvFile) {
      const result = await executeCreateFileImport({
        input: {
          fileName: csvFile.name,
          importIds: updatedResult.importIds,
        },
      })
      if (result.error) {
        logError(result.error.message, `File import could not be saved`)
      } else {
        debug(`imports`, `file imported`, result.data?.createFileImport)
      }
    }
    setProgress(100)
    delay(() => {
      setImporting(false)
      setDisableNext(false)
      setImportResults(updatedResult)
    }, 500)
  }

  let handleNextStep = async () => {
    if (activeStep === totalSteps) {
      navigate(`/settings/imports`)
      onComplete()
    } else if (errors > 0) {
      setOpenErrorModal(true)
    } else {
      if (activeStep === 4) {
        setDisablePrev(true)
        setImporting(true)
        await handleImport()
      }
      let nextStep = activeStep < totalSteps ? activeStep + 1 : activeStep
      setActiveStep(nextStep)
    }
  }
  let handlePrevStep = () => {
    let prevStep = activeStep > 1 ? activeStep - 1 : activeStep
    if (errors > 0) setErrors(0)
    setActiveStep(prevStep)
  }

  let getNextButtonLabel = () => {
    let label: string
    switch (activeStep) {
      case 1:
        label = `Go to Fields Mapping`
        break
      case 2:
        label = errors > 0 ? `Correct Errors` : `Go to Location Mapping`
        break
      case 3:
        label = errors > 0 ? `Correct Errors` : `Go to Preview`
        break
      case 4:
        label = errors > 0 ? `Correct Errors` : `Start the Import`
        break
      case 5:
        label = `Go to the dashboard`
        break
      default:
        label = `Next`
        break
    }

    return label
  }

  let handleImportErrors = () => {
    setOpenErrorModal(true)
  }

  return (
    <div className="sticky bottom-0 flex h-12 shrink-0 items-center justify-between gap-2 border-t border-t-default bg-surface">
      <div className="flex h-full items-center justify-start gap-x-2 pl-10">
        <span className="shrink-0 text-ui-sm text-default">
          Step {activeStep} of {totalSteps}
        </span>
        <div className="w-36">
          <ProgressBar
            progress={Math.floor((activeStep / totalSteps) * 100)}
            progressBarClassname="!h-[6px] bg-green-200"
          />
        </div>
      </div>
      <div className="pr-10">
        {activeStep > 1 && activeStep !== totalSteps ? (
          <Button label="Back" mode="white" size="sm" onClick={handlePrevStep} disabled={disablePrev} />
        ) : (
          activeStep === totalSteps &&
          importResults.error.length > 0 && (
            <Button label="Show Errors" mode="danger" size="sm" onClick={handleImportErrors} />
          )
        )}

        <Button
          label={getNextButtonLabel()}
          mode={errors > 0 ? `danger` : `primary`}
          size="sm"
          onClick={handleNextStep}
          className="ml-4"
          disabled={disableNext}
        />
      </div>
    </div>
  )
}

ImportWizard.Steps = ImportWizardSteps
ImportWizard.Buttons = ImportWizardButtons

export default ImportWizard
